import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Paper, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import { groupedEventsScores } from '../misc/Utils';

import { UserDataContext } from '../../firebase/UserDataContext';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { CommonDataContext } from '../../firebase/CommonDataContext';

const heightPerChart = 260;

const bucketScores = (scores, step = 100, max = 1500) => {
    const bins = Math.floor(max / step) + 1;
    const counts = Array(bins).fill(0);
    scores.forEach((s) => {
        const v = Number(s?.finalScore ?? 0);
        const clamped = Math.max(0, Math.min(max, v));
        const idx = Math.floor(clamped / step);
        counts[idx] += 1;
    });
    const labels = counts.map((_, i) => {
        const start = i * step;
        const end = Math.min(max, start + step - 1);
        return `${start}-${end}`;
    });
    return { labels, counts };
};

const ChartsPanel = ({ event }) => {
    const { defaultSelectedCompetition } = useContext(UserDataContext);
    const { firebase } = useContext(FirebaseNewContext);
    const { eventsMap: globalEventsMap } = useContext(CommonDataContext);

    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);

    const eventsMap = useMemo(() => {
        return globalEventsMap?.[defaultSelectedCompetition] || {};
    }, [globalEventsMap, defaultSelectedCompetition]);

    const getEventIdByNumber = (eventsMap, num) => {
        const entry = Object.entries(eventsMap).find(([, v]) => Number(v?.number) === Number(num));
        return entry ? entry[0] : null;
    };

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            setLoading(true);
            try {
                const scoresMapResult = await firebase.getScores(defaultSelectedCompetition);
                const eventsScoresMap = groupedEventsScores(scoresMapResult, eventsMap);
                const id = getEventIdByNumber(eventsMap, event?.number);
                const eventScores = eventsScoresMap[id]?.scores || [];
                if (mounted) setScores(eventScores);
            } catch (e) {
                if (mounted) setScores([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        console.log(event);
        if (event?.number && defaultSelectedCompetition) run();
        return () => {
            mounted = false;
        };
    }, [firebase, defaultSelectedCompetition, event?.number, eventsMap, event]);

    const histoSeries = useMemo(() => {
        const { labels, counts } = bucketScores(scores, 100, 1500);
        return { labels, series: [{ name: 'Teams', data: counts }] };
    }, [scores]);

    return (
        <Box
            sx={{
                height: '100%',
                minWidth: 600,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1,
            }}
        >
            <Stack spacing={2} sx={{ py: 1 }}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Score Distribution (0–1500)
                    </Typography>
                    <Box sx={{ height: heightPerChart }}>
                        <Chart
                            type="bar"
                            height={heightPerChart}
                            series={histoSeries.series}
                            options={{
                                chart: { id: 'dist-histo' },
                                xaxis: { categories: histoSeries.labels, tickAmount: 7, title: { text: 'Score Bucket' } },
                                yaxis: { title: { text: 'Count' } },
                                dataLabels: { enabled: false },
                                noData: { text: loading ? 'Loading…' : 'No data' },
                            }}
                        />
                    </Box>
                </Paper>
            </Stack>
        </Box>
    );
};

export default ChartsPanel;
